import type { Action } from 'types';
import { ACTIONS_CONFIG_PAYLOAD_ENUM, ACTIONS_ENUM } from 'types/enums';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function getPropertySafely(obj: unknown, key: string): unknown {
  if (!isObject(obj)) return undefined;
  return obj[key];
}

export function isCreateAction(action: Action, type: ACTIONS_ENUM): action is Action & { createAction: NonNullable<Action['createAction']> } {
  if (type !== ACTIONS_ENUM.Create) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.Create];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'values'));
}

export function isChangeAction(action: Action, type: ACTIONS_ENUM): action is Action & { changeAction: NonNullable<Action['changeAction']> } {
  if (type !== ACTIONS_ENUM.Change) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.Change];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'values'));
}

export function isDeleteNotificationAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { deleteNotificationAction: NonNullable<Action['deleteNotificationAction']> } {
  if (type !== ACTIONS_ENUM.DeleteNotification) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.DeleteNotification];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload);
}

export function isNotifyAction(action: Action, type: ACTIONS_ENUM): action is Action & { notifyAction: NonNullable<Action['notifyAction']> } {
  if (type !== ACTIONS_ENUM.Notify) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.Notify];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'values'));
}

export function isRedirectAction(action: Action, type: ACTIONS_ENUM): action is Action & { redirectAction: NonNullable<Action['redirectAction']> } {
  if (type !== ACTIONS_ENUM.Redirect) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.Redirect];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload);
}

export function isDistinctForeachAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { distinctForeachAction: NonNullable<Action['distinctForeachAction']> } {
  if (type !== ACTIONS_ENUM.DistinctForeach) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.DistinctForeach];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'values'));
}

export function isCreateForEachAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { createForEachAction: NonNullable<Action['createForEachAction']> } {
  if (type !== ACTIONS_ENUM.CreateForEach) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.CreateForEach];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'values'));
}

export function isSendDataToErpAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { sendDataToErpAction: NonNullable<Action['sendDataToErpAction']> } {
  if (type !== ACTIONS_ENUM.SendDataToErp) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.SendDataToErp];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'propertyPairing'));
}

export function isTakeDataFromErpAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { takeDataFromErpAction: NonNullable<Action['takeDataFromErpAction']> } {
  if (type !== ACTIONS_ENUM.TakeDataFromErp) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.TakeDataFromErp];
  const payload = getPropertySafely(action, payloadKey);
  const propertyPairing = getPropertySafely(payload, 'propertyPairing');
  const filter = getPropertySafely(payload, 'filter');
  return isObject(payload) && isArray(propertyPairing) && isArray(filter);
}

export function isExternalAction(action: Action, type: ACTIONS_ENUM): action is Action & { externalAction: NonNullable<Action['externalAction']> } {
  if (type !== ACTIONS_ENUM.ExternalAction) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.ExternalAction];
  const payload = getPropertySafely(action, payloadKey);
  const inputParameters = getPropertySafely(payload, 'inputParameters');
  const outputParameters = getPropertySafely(payload, 'outputParameters');
  return isObject(payload) && isArray(inputParameters) && isArray(outputParameters);
}

export function isExternalRestApiAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { externalRestApi: NonNullable<Action['externalRestApi']> } {
  if (type !== ACTIONS_ENUM.ExternalRestApi) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.ExternalRestApi];
  const payload = getPropertySafely(action, payloadKey);
  const headers = getPropertySafely(payload, 'headers');
  const inputValues = getPropertySafely(payload, 'inputValues');
  const outputValues = getPropertySafely(payload, 'outputValues');
  return isObject(payload) && isArray(headers) && isArray(inputValues) && isArray(outputValues);
}

export function isGoToExternalCatalogueAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { goToExternalCatalogueAction: NonNullable<Action['goToExternalCatalogueAction']> } {
  if (type !== ACTIONS_ENUM.GoToExternalCatalogue) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.GoToExternalCatalogue];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && getPropertySafely(payload, 'initialPairing') !== undefined;
}

export function isPurchaseExternalCatalogueAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { purchaseExternalCatalogueAction: NonNullable<Action['purchaseExternalCatalogueAction']> } {
  if (type !== ACTIONS_ENUM.PurchaseExternalCatalogue) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.PurchaseExternalCatalogue];
  const payload = getPropertySafely(action, payloadKey);
  return (
    isObject(payload) &&
    getPropertySafely(payload, 'sendHeader') !== undefined &&
    getPropertySafely(payload, 'sendItems') !== undefined &&
    getPropertySafely(payload, 'receiveHeader') !== undefined &&
    getPropertySafely(payload, 'receiveItems') !== undefined
  );
}

export function isDialogAction(action: Action, type: ACTIONS_ENUM): action is Action & { dialogAction: NonNullable<Action['dialogAction']> } {
  if (type !== ACTIONS_ENUM.Dialog) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.Dialog];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && getPropertySafely(payload, 'titleInput') !== undefined;
}

export function safeMapArray<T, R>(arr: T[], mapper: (item: T) => R): R[] {
  if (!isArray(arr)) return [];
  return arr.map(mapper);
}

export function isSendDataToErpWithItemsAction(
  action: Action,
  type: ACTIONS_ENUM
): action is Action & { sendDataToErpWithItemsAction: NonNullable<Action['sendDataToErpWithItemsAction']> } {
  if (type !== ACTIONS_ENUM.SendDataToErpWithItems) return false;
  const payloadKey = ACTIONS_CONFIG_PAYLOAD_ENUM[ACTIONS_ENUM.SendDataToErpWithItems];
  const payload = getPropertySafely(action, payloadKey);
  return isObject(payload) && isArray(getPropertySafely(payload, 'propertyPairing')) && isArray(getPropertySafely(payload, 'itemsPropertyPairing'));
}
